// ─────────────────────────────────────────────────────────
// services/offline.ts — Offline-first persistence helpers
//
// Gold Standard Migration: write-queue IDB (cbd_offline)
// replaced with Dexie write_queue table inside cbd_data.
// All exported function names and signatures are unchanged —
// callers (repository.ts, syncEngine.ts) need zero edits.
//
// Architecture:
//   Primary store : IndexedDB (cbd_data DB) via dexieClient.ts
//   Legacy store  : sessionStorage (STORE) — kept as fallback
//   Write queue   : dexieDb.write_queue (was cbd_offline DB)
// ─────────────────────────────────────────────────────────
import { useUIStore }   from '@/store/uiStore'
import { useDataStore } from '@/store/dataStore'
import { dexieDb }      from './dexieClient'

// ══════════════════════════════════════════════════════════
// STORE — sessionStorage + in-memory fallback (UNCHANGED)
// Kept for backward compat. IDB is now the primary store.
// ══════════════════════════════════════════════════════════
const _memFallback: Record<string, unknown> = {}
const KEY_PREFIX = 're_'

export const STORE = {
  get<T>(key: string): T | null {
    try {
      const v = sessionStorage.getItem(KEY_PREFIX + key)
      if (v !== null) return JSON.parse(v) as T
    } catch { /* blocked by browser */ }
    const mem = _memFallback[KEY_PREFIX + key]
    return mem !== undefined ? (mem as T) : null
  },

  set(key: string, value: unknown): void {
    try {
      sessionStorage.setItem(KEY_PREFIX + key, JSON.stringify(value))
    } catch { /* blocked by browser */ }
    _memFallback[KEY_PREFIX + key] = value
  },

  remove(key: string): void {
    try {
      sessionStorage.removeItem(KEY_PREFIX + key)
    } catch { /* blocked by browser */ }
    delete _memFallback[KEY_PREFIX + key]
  },
}

// ── Keys for sessionStorage snapshot ─────────────────────
const PERSIST_KEYS = [
  'proj', 'tender', 'emd', 'recv', 'exp', 'cf', 'vendor',
  'invoice', 'emp', 'att', 'sal', 'adv', 'ins', 'users',
  'receipts', 'task', 'boq', 'daily', 'audit', 'retRelease',
  'tdsChallans', 'bankTxns', 'einvoices', 'pettyCash', 'po',
  'milestones', 'quotations',
] as const

// ── persist() — snapshot Zustand → sessionStorage + IDB ──
// Called by fbSave when DB is disabled or offline.
export function persist(): void {
  const data = useDataStore.getState()
  const map: Record<string, unknown> = {
    proj: data.proj, tender: data.tender, emd: data.emd,
    recv: data.recv, exp: data.exp, cf: data.cf,
    vendor: data.vendor, invoice: data.invoice, emp: data.emp,
    att: data.att, sal: data.sal, adv: data.adv, ins: data.ins,
    task: data.task, boq: data.boq, daily: data.daily,
    audit: data.audit, retRelease: data.ret, einvoices: data.einvoices,
    pettyCash: data.petty, po: data.po, milestones: data.milestones,
    quotations: data.quotations, bankTxns: data.recon, tdsChallans: data.tds,
  }

  // 1. sessionStorage (sync, backward compat)
  PERSIST_KEYS.forEach(k => { if (map[k] !== undefined) STORE.set(k, map[k]) })

  // 2. IDB (async, fire-and-forget — primary from Phase 4)
  // We only write to IDB if we have a companyId from meta
  import('./idb').then(({ idbGetMeta }) => idbGetMeta()).then(meta => {
    if (!meta?.companyId) return
    import('./idbSync').then(({ idbWrite }) => {
      // Build a minimal LoadedData from current Zustand state
      idbWrite(meta.companyId, {
        proj: data.proj, boq: data.boq, milestones: data.milestones,
        daily: data.daily, tender: data.tender, emd: data.emd,
        recv: data.recv, ret: data.ret, exp: data.exp, cf: data.cf,
        petty: data.petty, recon: data.recon, vendor: data.vendor,
        po: data.po, quotations: data.quotations, invoice: data.invoice,
        emp: data.emp, att: data.att, sal: data.sal, adv: data.adv,
        ins: data.ins, tds: data.tds, einvoices: data.einvoices,
        task: data.task, audit: data.audit, notif: data.notif, brand: {},
      }).catch(e => console.warn('[offline] IDB persist failed:', e))
    })
  }).catch(() => { /* IDB unavailable, sessionStorage already written */ })
}

// ── restoreFromCache() — kept for offline fallback ────────
// Phase 4: primary restore happens via idbLoad() in auth.ts.
// This is a safety net if IDB is unavailable.
export function restoreFromCache(): boolean {
  if (navigator.onLine) {
    STORE.set('cu', null)
    STORE.set('companyId', null)
    return false
  }

  const data   = useDataStore.getState()
  let restored = false

  PERSIST_KEYS.forEach(k => {
    const v = STORE.get(k)
    if (v === null) return
    restored = true
    switch (k) {
      case 'proj':       data.setProj(v as Parameters<typeof data.setProj>[0]); break
      case 'tender':     data.setTender(v as Parameters<typeof data.setTender>[0]); break
      case 'emd':        data.setEmd(v as Parameters<typeof data.setEmd>[0]); break
      case 'recv':       data.setRecv(v as Parameters<typeof data.setRecv>[0]); break
      case 'exp':        data.setExp(v as Parameters<typeof data.setExp>[0]); break
      case 'cf':         data.setCf(v as Parameters<typeof data.setCf>[0]); break
      case 'vendor':     data.setVendor(v as Parameters<typeof data.setVendor>[0]); break
      case 'invoice':    data.setInvoice(v as Parameters<typeof data.setInvoice>[0]); break
      case 'emp':        data.setEmp(v as Parameters<typeof data.setEmp>[0]); break
      case 'adv':        data.setAdv(v as Parameters<typeof data.setAdv>[0]); break
      case 'ins':        data.setIns(v as Parameters<typeof data.setIns>[0]); break
      case 'task':       data.setTask(v as Parameters<typeof data.setTask>[0]); break
      case 'daily':      data.setDaily(v as Parameters<typeof data.setDaily>[0]); break
      case 'milestones': data.setMilestones(v as Parameters<typeof data.setMilestones>[0]); break
      case 'po':         data.setPo(v as Parameters<typeof data.setPo>[0]); break
      case 'quotations': data.setQuotations(v as Parameters<typeof data.setQuotations>[0]); break
    }
  })

  return restored
}

// ── showSync() — sync banner helper ──────────────────────
export function showSync(msg: string, done: boolean): void {
  const { setSyncing } = useUIStore.getState()
  setSyncing(!done, msg)
  if (done) setTimeout(() => setSyncing(false), 2000)
}

// ══════════════════════════════════════════════════════════
// Write Queue (Dexie write_queue table in cbd_data DB)
// Previously: separate cbd_offline IndexedDB database.
// Merged into the main Dexie DB at version 2.
// ══════════════════════════════════════════════════════════

// Retry backoff delays indexed by current attempt count (before increment):
//   attempt 0 → 1: wait 2s
//   attempt 1 → 2: wait 4s
//   attempt 2+:    dead-letter on next flush
const RETRY_DELAYS_MS: Record<number, number> = { 0: 2_000, 1: 4_000 }

export interface QueuedWrite {
  id?:         number  // auto-increment (optional on insert)
  col:         string
  docId:       string
  data:        object
  companyId:   string
  queuedAt:    string
  attempts:    number
  retryAfter?: string   // ISO timestamp — skip item until this time
}

export async function enqueueWrite(
  col: string, docId: string, data: object, companyId: string
): Promise<void> {
  try {
    const rec: Omit<QueuedWrite, 'id'> = {
      col, docId, data, companyId,
      queuedAt: new Date().toISOString(), attempts: 0,
    }
    await dexieDb.write_queue.add(rec as QueuedWrite)
  } catch (e) { console.warn('[offline] enqueueWrite failed:', e) }
}

export async function getQueue(): Promise<QueuedWrite[]> {
  try {
    return await dexieDb.write_queue.toArray()
  } catch { return [] }
}

// Max retry attempts before an item is considered dead and dropped.
const MAX_ATTEMPTS = 3

async function dequeue(id: number): Promise<void> {
  try {
    await dexieDb.write_queue.delete(id)
  } catch { /* ignore */ }
}

async function incrementAttempts(item: QueuedWrite): Promise<void> {
  try {
    const delayMs    = RETRY_DELAYS_MS[item.attempts] ?? 0
    const retryAfter = delayMs
      ? new Date(Date.now() + delayMs).toISOString()
      : undefined
    await dexieDb.write_queue.update(item.id!, { attempts: item.attempts + 1, retryAfter })
  } catch { /* ignore */ }
}

export async function flushQueue(): Promise<void> {
  if (!navigator.onLine) return
  const items = await getQueue()
  if (items.length === 0) return

  const { setSyncing } = useUIStore.getState()
  setSyncing(true, `Syncing ${items.length} offline write${items.length > 1 ? 's' : ''}…`)

  const { fbSave } = await import('./db')
  let flushed = 0
  let deadLettered = 0

  for (const item of items) {
    // Drop items that have exceeded max attempts (dead-letter)
    if (item.attempts >= MAX_ATTEMPTS) {
      console.warn(
        `[offline] Dropping dead-letter write after ${MAX_ATTEMPTS} attempts:`,
        item.col, item.docId
      )
      await dequeue(item.id!)
      deadLettered++
      continue
    }

    // Skip items still within their backoff window
    if (item.retryAfter && new Date(item.retryAfter) > new Date()) continue

    try {
      await fbSave(item.col, item.docId, item.data, item.companyId)
      await dequeue(item.id!)
      flushed++
    } catch {
      // Increment attempts + set next retryAfter — retried on next flush
      await incrementAttempts(item)
    }
  }

  const actionable = items.length - deadLettered
  const msg = flushed === actionable
    ? `Synced ${flushed} offline write${flushed !== 1 ? 's' : ''} ✓`
    : `Synced ${flushed}/${actionable}${deadLettered > 0 ? ` (${deadLettered} dropped after ${MAX_ATTEMPTS} retries)` : ' — some writes still pending'}`

  setSyncing(true, msg)
  setTimeout(() => setSyncing(false), 3000)

  // Persist a visible banner if any writes were dead-lettered
  if (deadLettered > 0) {
    useUIStore.getState().setSyncError(
      `${deadLettered} write${deadLettered !== 1 ? 's' : ''} failed after ${MAX_ATTEMPTS} attempts and could not be synced. Some data may be missing from the server.`
    )
  }
}

export async function queueSize(): Promise<number> {
  try {
    return await dexieDb.write_queue.count()
  } catch { return 0 }
}
